import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAdmin } from '@/context/AdminContext';
import { useAdminTheme } from '@/context/AdminThemeContext';
import { adminService } from '@/services/adminApi';
import AdminSidebar from '@/components/admin/AdminSidebar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Users, FileText, Activity, AlertTriangle, 
  RefreshCw, TrendingUp, Calendar, Clock, Download, 
  MessageSquare, Zap, Server, ChevronRight, Menu, Globe,
  Brain, UserCheck, Target, BarChart3, PieChart as PieChartIcon, Smartphone
} from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell, Legend
} from 'recharts';

// Colors
const COLORS = {
  primary: '#5BA946',
  blue: '#3b82f6',
  purple: '#8b5cf6',
  orange: '#f59e0b',
  red: '#ef4444',
  teal: '#06b6d4',
  pink: '#ec4899'
};

const COUNTRY_COLORS = [
  COLORS.primary, COLORS.blue, COLORS.purple, COLORS.orange, COLORS.red,
  COLORS.teal, COLORS.pink, '#84cc16', '#f97316', '#6366f1'
];

// Stat Card Component
function StatCard({ title, value, subtitle, icon: Icon, color, colors, trend }) {
  const colorClasses = {
    green: 'bg-green-500/10 text-green-500',
    blue: 'bg-blue-500/10 text-blue-500',
    purple: 'bg-purple-500/10 text-purple-500',
    orange: 'bg-orange-500/10 text-orange-500',
    red: 'bg-red-500/10 text-red-500',
    teal: 'bg-teal-500/10 text-teal-500',
    pink: 'bg-pink-500/10 text-pink-500',
  };

  return (
    <Card className={`${colors.bgCard} ${colors.border} hover:shadow-lg transition-shadow`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <p className={`${colors.textSecondary} text-xs font-medium truncate`}>{title}</p>
            <p className={`text-2xl font-bold ${colors.text} mt-1`}>{value}</p>
            {subtitle && (
              <p className={`text-xs ${colors.textMuted} mt-1`}>{subtitle}</p>
            )}
            {trend !== undefined && (
              <div className={`flex items-center mt-1 text-xs ${trend >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                <TrendingUp className={`w-3 h-3 mr-1 ${trend < 0 ? 'rotate-180' : ''}`} />
                {Math.abs(trend)}% vs hier
              </div>
            )}
          </div>
          <div className={`p-2 rounded-lg ${colorClasses[color] || colorClasses.blue} flex-shrink-0`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Mini Chart Component
function MiniDonut({ data, colors: themeColors }) {
  const chartData = Object.entries(data || {}).filter(([_, v]) => v > 0).map(([name, value]) => ({ name, value }));
  
  if (chartData.length === 0) {
    return <div className={`text-center ${themeColors.textMuted} py-4`}>Pas de données</div>;
  }

  return (
    <ResponsiveContainer width="100%" height={120}>
      <PieChart>
        <Pie
          data={chartData}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          innerRadius={25}
          outerRadius={45}
        >
          {chartData.map((_, index) => (
            <Cell key={index} fill={Object.values(COLORS)[index % Object.values(COLORS).length]} />
          ))}
        </Pie>
        <Tooltip />
      </PieChart>
    </ResponsiveContainer>
  );
}

export default function AdminDashboard() {
  const { isAuthenticated, loading: authLoading } = useAdmin();
  const { colors, isDark } = useAdminTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [downloadExportStats, setDownloadExportStats] = useState(null);
  const [usersChart, setUsersChart] = useState([]);
  const [sessionsChart, setSessionsChart] = useState([]);
  const [countryStats, setCountryStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      loadData();
      const refreshInterval = setInterval(() => loadData(), 30000);
      return () => clearInterval(refreshInterval);
    }
  }, [isAuthenticated]);

  const loadData = async () => {
    try {
      if (!refreshing) setLoading(true);
      const [statsRes, downloadExportRes, usersRes, sessionsRes, countriesRes] = await Promise.all([
        adminService.getStats(),
        adminService.getDownloadExportStats(),
        adminService.getUsersChart(14),
        adminService.getSessionsChart(14),
        adminService.getUsersByCountry()
      ]);
      
      setStats(statsRes.data);
      setDownloadExportStats(downloadExportRes.data);
      setUsersChart(usersRes.data);
      setSessionsChart(sessionsRes.data);
      setCountryStats(countriesRes.data || []);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      toast.error('Erreur de chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
    toast.success('Dashboard actualisé');
  };

  if (authLoading || loading) {
    return (
      <div className={`min-h-screen ${colors.bg} flex items-center justify-center`}>
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#5BA946] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className={colors.textSecondary}>Chargement du dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${colors.bg} flex`}>
      <AdminSidebar 
        currentPath={location.pathname}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      
      <main className="flex-1 overflow-auto lg:ml-0">
        {/* Header */}
        <header className={`${colors.bgHeader} border-b ${colors.border} p-4 sticky top-0 z-10`}>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Button
                onClick={() => setSidebarOpen(true)}
                variant="ghost"
                size="icon"
                className={`lg:hidden ${colors.textSecondary}`}
              >
                <Menu className="w-6 h-6" />
              </Button>
              <div>
                <h1 className={`text-xl font-bold ${colors.text}`}>📊 Dashboard EdoMatch</h1>
                {lastUpdated && (
                  <p className={`${colors.textMuted} text-xs mt-1`}>
                    Mis à jour : {lastUpdated.toLocaleTimeString('fr-FR')} • Auto-refresh 30s
                  </p>
                )}
              </div>
            </div>
            <Button 
              onClick={handleRefresh}
              disabled={refreshing}
              variant="outline"
              size="sm"
              className={colors.btnOutline}
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''} mr-2`} />
              Actualiser
            </Button>
          </div>
        </header>

        <div className="p-4 space-y-6">
          {/* ===== ROW 1: Key Metrics ===== */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <StatCard
              title="Utilisateurs Actifs (Jour)"
              value={stats?.daily_active_users || 0}
              subtitle={`${stats?.monthly_active_users || 0} ce mois`}
              icon={Users}
              color="blue"
              colors={colors}
            />
            <StatCard
              title="CVs Créés"
              value={stats?.total_cvs || 0}
              subtitle={`+${stats?.cvs_created_today || 0} aujourd'hui`}
              icon={FileText}
              color="green"
              colors={colors}
            />
            <StatCard
              title="Tests Personnalité"
              value={stats?.personality_tests_completed || 0}
              subtitle={`+${stats?.personality_tests_today || 0} aujourd'hui`}
              icon={Brain}
              color="purple"
              colors={colors}
            />
            <StatCard
              title="Téléchargements"
              value={stats?.total_downloads || 0}
              subtitle={`+${stats?.downloads_today || 0} aujourd'hui`}
              icon={Download}
              color="orange"
              colors={colors}
            />
            <StatCard
              title="Leads Capturés"
              value={stats?.total_leads || 0}
              subtitle={`${stats?.conversion_rate || 0}% conversion`}
              icon={Target}
              color="teal"
              colors={colors}
            />
            <StatCard
              title="Erreurs Non Résolues"
              value={stats?.unresolved_errors || 0}
              subtitle={`${stats?.errors_today || 0} aujourd'hui`}
              icon={AlertTriangle}
              color={stats?.unresolved_errors > 0 ? 'red' : 'green'}
              colors={colors}
            />
          </div>

          {/* ===== ROW 2: Chat & Session Metrics ===== */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard
              title="Messages AI Chat"
              value={stats?.total_messages || 0}
              subtitle={`+${stats?.messages_today || 0} aujourd'hui`}
              icon={MessageSquare}
              color="blue"
              colors={colors}
            />
            <StatCard
              title="Moy. Messages/Session"
              value={stats?.avg_messages_per_session?.toFixed(1) || '0'}
              icon={BarChart3}
              color="purple"
              colors={colors}
            />
            <StatCard
              title="Sessions Actives"
              value={stats?.active_sessions || 0}
              icon={Zap}
              color="orange"
              colors={colors}
            />
            <StatCard
              title="Durée Moy. Session"
              value={`${stats?.avg_session_duration_minutes?.toFixed(1) || 0} min`}
              icon={Clock}
              color="teal"
              colors={colors}
            />
          </div>

          {/* ===== ROW 2.5: CV Downloads & App Exports ===== */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard
              title="CV Téléchargés"
              value={downloadExportStats?.cv_downloaded_count || 0}
              subtitle={`${downloadExportStats?.cv_downloaded_percentage || 0}% des profils`}
              icon={Download}
              color="green"
              colors={colors}
            />
            <StatCard
              title="Total Downloads"
              value={downloadExportStats?.total_downloads || 0}
              subtitle={`+${downloadExportStats?.downloads_today || 0} aujourd'hui`}
              icon={Download}
              color="blue"
              colors={colors}
            />
            <StatCard
              title="Exportés vers App"
              value={downloadExportStats?.app_exported_count || 0}
              subtitle={`${downloadExportStats?.app_exported_percentage || 0}% des profils`}
              icon={Smartphone}
              color="purple"
              colors={colors}
            />
            <StatCard
              title="Total Exports App"
              value={downloadExportStats?.total_exports || 0}
              subtitle={`+${downloadExportStats?.exports_today || 0} aujourd'hui`}
              icon={Smartphone}
              color="pink"
              colors={colors}
            />
          </div>

          {/* ===== ROW 3: Charts ===== */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Users Chart */}
            <Card className={`${colors.bgCard} ${colors.border}`}>
              <CardHeader className="pb-2">
                <CardTitle className={`${colors.text} text-sm flex items-center gap-2`}>
                  <Users className="w-4 h-4 text-blue-500" />
                  Nouveaux Utilisateurs (14 jours)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={usersChart}>
                      <defs>
                        <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} />
                      <XAxis 
                        dataKey="date" 
                        stroke={isDark ? '#6b7280' : '#9ca3af'}
                        fontSize={10}
                        tickFormatter={(value) => value.slice(5)}
                      />
                      <YAxis stroke={isDark ? '#6b7280' : '#9ca3af'} fontSize={10} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: isDark ? '#1f2937' : '#fff',
                          border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
                          borderRadius: '8px',
                          fontSize: '12px'
                        }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="value" 
                        stroke="#3b82f6" 
                        fillOpacity={1}
                        fill="url(#colorUsers)" 
                        name="Utilisateurs"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Sessions Chart */}
            <Card className={`${colors.bgCard} ${colors.border}`}>
              <CardHeader className="pb-2">
                <CardTitle className={`${colors.text} text-sm flex items-center gap-2`}>
                  <MessageSquare className="w-4 h-4 text-green-500" />
                  Sessions (14 jours)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={sessionsChart}>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} />
                      <XAxis 
                        dataKey="date" 
                        stroke={isDark ? '#6b7280' : '#9ca3af'}
                        fontSize={10}
                        tickFormatter={(value) => value.slice(5)}
                      />
                      <YAxis stroke={isDark ? '#6b7280' : '#9ca3af'} fontSize={10} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: isDark ? '#1f2937' : '#fff',
                          border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
                          borderRadius: '8px',
                          fontSize: '12px'
                        }}
                      />
                      <Bar 
                        dataKey="value" 
                        fill="#5BA946" 
                        radius={[4, 4, 0, 0]}
                        name="Sessions"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* ===== ROW 4: Downloads & Errors Breakdown ===== */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Downloads by Type */}
            <Card className={`${colors.bgCard} ${colors.border}`}>
              <CardHeader className="pb-2">
                <CardTitle className={`${colors.text} text-sm flex items-center gap-2`}>
                  <Download className="w-4 h-4 text-orange-500" />
                  Téléchargements par Type
                </CardTitle>
              </CardHeader>
              <CardContent>
                <MiniDonut data={stats?.downloads_by_type} colors={colors} />
                <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                  <div className={`${colors.textSecondary}`}>CV: <span className={colors.text}>{stats?.downloads_by_type?.cv || 0}</span></div>
                  <div className={`${colors.textSecondary}`}>Personnalité: <span className={colors.text}>{stats?.downloads_by_type?.personality || 0}</span></div>
                  <div className={`${colors.textSecondary}`}>Feedback: <span className={colors.text}>{stats?.downloads_by_type?.feedback || 0}</span></div>
                  <div className={`${colors.textSecondary}`}>Complet: <span className={colors.text}>{stats?.downloads_by_type?.complete || 0}</span></div>
                </div>
              </CardContent>
            </Card>

            {/* Errors by Type */}
            <Card className={`${colors.bgCard} ${colors.border}`}>
              <CardHeader className="pb-2">
                <CardTitle className={`${colors.text} text-sm flex items-center gap-2`}>
                  <AlertTriangle className="w-4 h-4 text-red-500" />
                  Erreurs par Type
                </CardTitle>
              </CardHeader>
              <CardContent>
                <MiniDonut data={stats?.errors_by_type} colors={colors} />
                <div className="space-y-1 mt-2 text-xs">
                  {Object.entries(stats?.errors_by_type || {}).slice(0, 4).map(([type, count]) => (
                    <div key={type} className="flex justify-between">
                      <span className={colors.textSecondary}>{type}</span>
                      <span className={`${colors.text} font-medium`}>{count}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Country Distribution */}
            <Card className={`${colors.bgCard} ${colors.border}`}>
              <CardHeader className="pb-2">
                <CardTitle className={`${colors.text} text-sm flex items-center gap-2`}>
                  <Globe className="w-4 h-4 text-teal-500" />
                  Utilisateurs par Pays
                </CardTitle>
              </CardHeader>
              <CardContent>
                {countryStats.length > 0 ? (
                  <>
                    <div className="h-28">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={countryStats.slice(0, 5)}
                            dataKey="count"
                            nameKey="country"
                            cx="50%"
                            cy="50%"
                            innerRadius={20}
                            outerRadius={40}
                          >
                            {countryStats.slice(0, 5).map((_, index) => (
                              <Cell key={index} fill={COUNTRY_COLORS[index]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-1 text-xs">
                      {countryStats.slice(0, 3).map((c, i) => (
                        <div key={i} className="flex justify-between">
                          <span className={colors.textSecondary}>{c.country}</span>
                          <span className={colors.text}>{c.count}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className={`h-32 flex items-center justify-center ${colors.textMuted} text-xs`}>
                    Pas de données pays
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* ===== ROW 5: API Health ===== */}
          <Card className={`${colors.bgCard} ${colors.border}`}>
            <CardHeader className="pb-2">
              <CardTitle className={`${colors.text} text-sm flex items-center gap-2`}>
                <Server className="w-4 h-4 text-purple-500" />
                État des Services API
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {Object.entries(stats?.api_health || {}).map(([service, status]) => (
                  <div 
                    key={service}
                    className={`p-3 ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'} rounded-lg text-center`}
                  >
                    <div className={`w-2.5 h-2.5 rounded-full mx-auto mb-2 ${
                      status === 'healthy' ? 'bg-green-500' :
                      status === 'degraded' ? 'bg-yellow-500' :
                      status === 'down' ? 'bg-red-500' :
                      'bg-slate-400'
                    }`} />
                    <p className={`text-xs ${colors.text} font-medium capitalize`}>
                      {service.replace('_', ' ')}
                    </p>
                    <p className={`text-xs mt-0.5 capitalize ${
                      status === 'healthy' ? 'text-green-500' :
                      status === 'degraded' ? 'text-yellow-500' :
                      status === 'down' ? 'text-red-500' :
                      colors.textMuted
                    }`}>
                      {status}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* ===== Quick Actions ===== */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Link to="/admin/users">
              <Card className={`${colors.bgCard} ${colors.border} ${colors.bgHover} transition-all cursor-pointer h-full`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-500/10 rounded-lg">
                      <Users className="w-5 h-5 text-blue-500" />
                    </div>
                    <div>
                      <p className={`${colors.text} font-medium text-sm`}>Utilisateurs</p>
                      <p className={`text-xs ${colors.textSecondary}`}>{stats?.total_users || 0} total</p>
                    </div>
                  </div>
                  <ChevronRight className={`w-4 h-4 ${colors.textMuted}`} />
                </CardContent>
              </Card>
            </Link>

            <Link to="/admin/leads">
              <Card className={`${colors.bgCard} ${colors.border} ${colors.bgHover} transition-all cursor-pointer h-full`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-teal-500/10 rounded-lg">
                      <Target className="w-5 h-5 text-teal-500" />
                    </div>
                    <div>
                      <p className={`${colors.text} font-medium text-sm`}>Leads</p>
                      <p className={`text-xs ${colors.textSecondary}`}>{stats?.total_leads || 0} capturés</p>
                    </div>
                  </div>
                  <ChevronRight className={`w-4 h-4 ${colors.textMuted}`} />
                </CardContent>
              </Card>
            </Link>

            <Link to="/admin/errors">
              <Card className={`${colors.bgCard} ${colors.border} ${colors.bgHover} transition-all cursor-pointer h-full`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${stats?.unresolved_errors > 0 ? 'bg-red-500/10' : 'bg-green-500/10'}`}>
                      <AlertTriangle className={`w-5 h-5 ${stats?.unresolved_errors > 0 ? 'text-red-500' : 'text-green-500'}`} />
                    </div>
                    <div>
                      <p className={`${colors.text} font-medium text-sm`}>Erreurs</p>
                      <p className={`text-xs ${colors.textSecondary}`}>{stats?.unresolved_errors || 0} non résolues</p>
                    </div>
                  </div>
                  <ChevronRight className={`w-4 h-4 ${colors.textMuted}`} />
                </CardContent>
              </Card>
            </Link>

            <Link to="/admin/health">
              <Card className={`${colors.bgCard} ${colors.border} ${colors.bgHover} transition-all cursor-pointer h-full`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-500/10 rounded-lg">
                      <Activity className="w-5 h-5 text-purple-500" />
                    </div>
                    <div>
                      <p className={`${colors.text} font-medium text-sm`}>Santé API</p>
                      <p className={`text-xs ${colors.textSecondary}`}>Voir détails</p>
                    </div>
                  </div>
                  <ChevronRight className={`w-4 h-4 ${colors.textMuted}`} />
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
